import React, { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Copy, Check } from 'lucide-react';
import { BlogPost } from '@/services/blogService';
import { CodeBlock, InlineCode } from '../BlogElements/CodeBlock';
import { LazyImage } from '../BlogUtils/LazyImage';
import { LazyComponent } from '../BlogUtils/LazyComponent';
// Performance monitoring removed
import { AccessibleHeading, AccessibleLink } from '../BlogUtils/AccessibilityUtils';
import { CalloutBox } from '../BlogElements/CalloutBox';
import { StatisticsCard } from '../BlogElements/StatisticsCard';
import { TestimonialCard } from '../BlogElements/TestimonialCard';

interface BlogContentProps {
  post: BlogPost;
  config: {
    enableSmoothScrolling: boolean;
  };
  children?: React.ReactNode;
}

const CopyButton: React.FC<{ code: string }> = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="absolute top-3 right-3 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors backdrop-blur-xl border border-white/10"
      title="Copy code"
    >
      {copied ? (
        <Check className="w-4 h-4 text-green-400" />
      ) : (
        <Copy className="w-4 h-4 text-white/70" />
      )}
    </button>
  );
};

export const BlogContent: React.FC<BlogContentProps> = memo(({ post, config, children }) => {
  // Performance optimized

  // Memoize markdown components to prevent re-renders
  const markdownComponents = useMemo(() => ({
            code({ node, className, children, ...props }) {
              const match = /language-(\w+)/.exec(className || '');
              const inline = !match;
              const code = String(children).replace(/\n$/, '');
              
              return !inline ? (
                <CodeBlock code={code} language={match[1]} />
              ) : (
                <InlineCode {...props}>
                  {children}
                </InlineCode>
              );
            },
            h1: ({ children, ...props }) => {
              const id = typeof children === 'string' 
                ? children.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                : '';
              return (
                <AccessibleHeading 
                  level={1}
                  id={id}
                  className="mt-16 mb-8 first:mt-0"
                  {...props}
                >
                  {children}
                </AccessibleHeading>
              );
            },
            h2: ({ children, ...props }) => {
              const id = typeof children === 'string' 
                ? children.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                : '';
              return (
                <AccessibleHeading 
                  level={2}
                  id={id}
                  className="mt-12 mb-6"
                  {...props}
                >
                  {children}
                </AccessibleHeading>
              );
            },
            h3: ({ children, ...props }) => {
              const id = typeof children === 'string' 
                ? children.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                : '';
              return (
                <AccessibleHeading 
                  level={3}
                  id={id}
                  className="mt-10 mb-4"
                  {...props}
                >
                  {children}
                </AccessibleHeading>
              );
            },
            h4: ({ children, ...props }) => {
              const id = typeof children === 'string' 
                ? children.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                : '';
              return (
                <AccessibleHeading 
                  level={4}
                  id={id}
                  className="mt-8 mb-3"
                  {...props}
                >
                  {children}
                </AccessibleHeading>
              );
            },
            p: ({ children }) => (
              <p className="text-white/80 leading-relaxed mb-6 text-lg">
                {children}
              </p>
            ),
            ul: ({ children }) => (
              <ul className="list-none space-y-3 mb-6">
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal list-inside text-white/80 mb-6 space-y-3 text-lg">
                {children}
              </ol>
            ),
            li: ({ children, ...props }) => (
              <li className="text-white/80 text-lg flex items-start" {...props}>
                <span className="inline-block w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full mt-3 mr-3 flex-shrink-0"></span>
                <span>{children}</span>
              </li>
            ),
            blockquote: ({ children }) => {
              const content = typeof children === 'string' ? children : 
                React.Children.toArray(children).map(child => 
                  typeof child === 'string' ? child : 
                  React.isValidElement(child) && child.props?.children ? 
                    extractTextFromElement(child) : ''
                ).join('');
              
              // Check for emoji indicators for callout types
              if (content.includes('💡') || content.includes('Pro Tip:')) {
                const cleanContent = content.replace(/^.*?(💡|Pro Tip:)\s*/i, '');
                return (
                  <CalloutBox type="tip" title="Pro Tip">
                    {cleanContent}
                  </CalloutBox>
                );
              }
              if (content.includes('⚠️') || content.includes('🚨') || content.includes('Important:')) {
                const cleanContent = content.replace(/^.*?(⚠️|🚨|Important:)\s*/i, '');
                return (
                  <CalloutBox type="warning" title="Important">
                    {cleanContent}
                  </CalloutBox>
                );
              }
              if (content.includes('✅') || content.includes('Success:')) {
                const cleanContent = content.replace(/^.*?(✅|Success:)\s*/i, '');
                return (
                  <CalloutBox type="success" title="Success">
                    {cleanContent}
                  </CalloutBox>
                );
              }
              if (content.includes('Reality Check:')) {
                const cleanContent = content.replace(/^.*?Reality Check:\s*/i, '');
                return (
                  <CalloutBox type="warning" title="Reality Check">
                    {cleanContent}
                  </CalloutBox>
                );
              }
              if (content.includes('Speed Matters:')) {
                const cleanContent = content.replace(/^.*?Speed Matters:\s*/i, '');
                return (
                  <CalloutBox type="info" title="Speed Matters">
                    {cleanContent}
                  </CalloutBox>
                );
              }
              
              // Default blockquote styling
              return (
                <blockquote className="border-l-4 border-gradient-to-b from-blue-500 to-purple-500 pl-6 py-4 my-8 bg-white/5 rounded-r-xl backdrop-blur-xl">
                  <div className="text-white/90 italic text-lg leading-relaxed">
                    {children}
                  </div>
                </blockquote>
              );
            },
            a: ({ href, children }) => (
              <AccessibleLink 
                href={href || '#'} 
                external={href?.startsWith('http')}
                ariaLabel={typeof children === 'string' ? `Link to ${children}` : undefined}
              >
                {children}
              </AccessibleLink>
            ),
            img: ({ src, alt }) => (
              <LazyComponent className="my-8">
                <div>
                  <LazyImage 
                    src={src || ''} 
                    alt={alt || ''} 
                    className="rounded-xl w-full shadow-2xl border border-white/10" 
                  />
                  {alt && (
                    <p className="text-center text-white/60 text-sm mt-3 italic">
                      {alt}
                    </p>
                  )}
                </div>
              </LazyComponent>
            ),
            table: ({ children }) => (
              <LazyComponent className="my-8">
                <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/5 backdrop-blur-xl">
                  <table className="w-full" role="table">
                    {children}
                  </table>
                </div>
              </LazyComponent>
            ),
            th: ({ children }) => (
              <th 
                className="px-6 py-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-white font-semibold text-left border-b border-white/10"
                scope="col"
              >
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className="px-6 py-4 text-white/80 border-b border-white/5 last:border-b-0">
                {children}
              </td>
            ),
            hr: () => (
              <hr className="my-12 border-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            ),
            strong: ({ children }) => (
              <strong className="text-white font-semibold">
                {children}
              </strong>
            ),
            em: ({ children }) => (
              <em className="text-blue-300 italic">
                {children}
              </em>
            ),
            // Custom div handler for statistics and testimonials
            div: ({ children, ...props }) => {
              const className = (props as any).className || '';
              
              // Handle statistics grid
              if (className.includes('statistics-grid') || className.includes('impact-stats')) {
                const statElements = React.Children.toArray(children).filter(
                  (child): child is React.ReactElement => 
                    React.isValidElement(child) && 
                    ((child.props as any)?.className?.includes('stat-card') || 
                     (child.props as any)?.className?.includes('stat-highlight'))
                );
                
                if (statElements.length > 0) {
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 my-8">
                      {statElements.map((child, index) => {
                        const content = extractTextFromElement(child);
                        const lines = content.split('\n').filter(Boolean);
                        const value = lines[0] || '';
                        const description = lines[1] || '';
                        
                        return (
                          <StatisticsCard
                            key={index}
                            title={description}
                            value={value}
                            trend="up"
                          />
                        );
                      })}
                    </div>
                  );
                }
              }
              
              // Handle individual stat cards
              if (className.includes('stat-card') || className.includes('stat-highlight')) {
                const content = extractTextFromElement({ props: { children } });
                const lines = content.split('\n').filter(Boolean);
                const value = lines[0] || '';
                const description = lines[1] || '';
                
                return (
                  <StatisticsCard
                    title={description}
                    value={value}
                    trend="up"
                  />
                );
              }
              
              // Handle testimonials grid
              if (className.includes('testimonials-grid')) {
                const testimonialElements = React.Children.toArray(children).filter(
                  (child): child is React.ReactElement =>
                    React.isValidElement(child) && 
                    (child.props as any)?.className?.includes('testimonial')
                );
                
                if (testimonialElements.length > 0) {
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                      {testimonialElements.map((child, index) => {
                        const content = extractTextFromElement(child);
                        const lines = content.split('\n').filter(Boolean);
                        const quote = lines[0] || '';
                        const author = lines[1]?.replace(/^[—-]\s*/, '') || 'Anonymous';
                        const role = lines[2] || '';
                        
                        return (
                          <TestimonialCard
                            key={index}
                            quote={quote}
                            author={author}
                            role={role}
                          />
                        );
                      })}
                    </div>
                  );
                }
              }
              
              // Handle individual testimonials
              if (className.includes('testimonial-highlight') || className.includes('testimonial')) {
                const content = extractTextFromElement({ props: { children } });
                const lines = content.split('\n').filter(Boolean);
                const quote = lines[0] || '';
                const author = lines[1]?.replace(/^[—-]\s*/, '') || 'Anonymous';
                const role = lines[2] || '';
                
                return (
                  <TestimonialCard
                    quote={quote}
                    author={author}
                    role={role}
                  />
                );
              }
              
              // Default div rendering
              return <div {...props}>{children}</div>;
            },
          }), []);

  // Helper function to extract text from React elements
  const extractTextFromElement = (element: any): string => {
    if (!element) return '';
    
    const { children } = element.props || {};
    
    if (typeof children === 'string') return children;
    if (typeof children === 'number') return String(children);
    
    if (Array.isArray(children)) {
      return children.map((child: any) => {
        if (typeof child === 'string') return child;
        if (typeof child === 'number') return String(child);
        if (React.isValidElement(child)) return extractTextFromElement(child);
        return '';
      }).join('');
    }
    
    if (React.isValidElement(children)) {
      return extractTextFromElement(children);
    }
    
    return '';
  };

  return (
    <div className="prose prose-lg prose-invert max-w-none" role="main" aria-label="Blog post content">
      {children || (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={markdownComponents}
        >
          {post.content}
        </ReactMarkdown>
      )}
    </div>
  );
});

BlogContent.displayName = 'BlogContent';

export default BlogContent;